import { minimumScreenHeight } from '../constants'

export function flattenNestedKeys(nestedObject, prefix = '') {
  if (!nestedObject) return {}

  return Object.keys(nestedObject).reduce((obj, key) => {
    const value = nestedObject[key]
    const prefixedKey = prefix ? `${prefix}.${key}` : key

    if (typeof value === 'string') {
      obj[prefixedKey] = value
    } else {
      Object.assign(obj, flattenNestedKeys(value, prefixedKey))
    }

    return obj
  }, {})
}

/**
 * Selects the most appropriate locale based on the input and a set of supported languages.
 *
 * @function
 * @param {string} locale - The desired locale to check for and potentially use.
 *                          Expected format: "<language_code>-<country_code>" (e.g., "en-US", "es-MX").
 * @param {Object} languageSupport - An object detailing the supported languages and their respective locales.
 *                                    Keys are language codes (e.g., "en", "es").
 *                                    Values are arrays of supported locales for the language (e.g., ["en-US"], ["es-ES", "es-MX"]).
 * @param {string} delimiter - The character used to separate the language code and the country code in the locale.
 *                             Typically, this is "-" or "_".
 * @returns {string} - The most appropriate locale based on the input and support:
 *                     - The exact input locale if it's supported.
 *                     - The primary (first listed) locale for the language if the exact locale isn't supported but the language is.
 *                     - The primary locale for English ("en") if neither the input locale nor its language is supported.
 *
 * @example
 * const languageSupport = {
 *   "en": ["en-US"],
 *   "es": ["es-ES", "es-MX"],
 *   "it": ["it-IT"],
 *   "zh": ["zh-CN"]
 * };
 *
 * selectAppropriateLocale("es-AR", languageSupport, '-');  // Returns: "es-ES"
 * selectAppropriateLocale("es-MX", languageSupport, '-');  // Returns: "es-MX"
 * selectAppropriateLocale("de-DE", languageSupport, '-');  // Returns: "en-US"
 **/
export function selectAppropriateLocale(locale, languageSupport, delimiter) {
  const localeParts = locale.split(delimiter) // [lang, locale]
  const localeList = languageSupport[localeParts[0]] // seeing if language support exists for a given language
  if (localeList) {
    //it does
    if (localeParts.length !== 2 || !localeList.includes(locale)) {
      return localeList[0] // exact locale is not found, so pick first locale in the list
    }
    return locale //exact locale is found and return
  }
  return languageSupport['en'][0] //unsupported locale/language, will switch to en_US
}

export function deepMerge(target, source) {
  const result = { ...target }

  Object.keys(source).forEach((key) => {
    const targetValue = result[key]
    const sourceValue = source[key]

    if (
      targetValue &&
      sourceValue &&
      typeof targetValue === 'object' &&
      typeof sourceValue === 'object' &&
      !Array.isArray(targetValue) &&
      !Array.isArray(sourceValue)
    ) {
      result[key] = deepMerge(targetValue, sourceValue)
    } else {
      result[key] = sourceValue
    }
  })

  return result
}

export const isWindowBelowMinHeight = () => {
  return window.innerHeight < minimumScreenHeight
}

export const prettyPrintFormData = (formData) => {
  // 1. Convert to a standard object
  const dataObject = Object.fromEntries(formData.entries())
  // 2. Pretty print to the console
  console.log(JSON.stringify(dataObject, null, 2))
}
